'use client';

/**
 * `useJourneyPianoBridge`, pipes piano events into the `LearningJourney`.
 *
 * `PianoEvent` does not carry a native timestamp (see `lib/piano/piano.ts`),
 * so the bridge stamps each event with `performance.now()` at the very
 * moment the listener fires. That is the closest the runtime can get
 * without modifying the piano API; the latency between the physical key
 * press and this callback is negligible compared to the rhythmic windows
 * (~50 ms tolerances) the detectors use.
 *
 * **Only the player's presses are recorded.** The piano is a shared bus: the
 * chord previews, the audition and the accompanied pass all sound notes on it
 * with `origin: "app"`. Without this filter the application answers its own
 * questions — its accompaniment would land in the capture and be read back as
 * the player having played in time.
 *
 * Pure side effect, no return value. Cleanup is automatic via the
 * `useEffect` teardown (the unsubscribe returned by `Piano.on`).
 */

import { useEffect } from 'react';
import type { LearningJourney } from '@/lib/learning-journey/journey';
import type { Studio } from '@/lib/studio/studio';

export function useJourneyPianoBridge(
  journey: LearningJourney | null,
  studio: Studio | null,
): void {
  useEffect(() => {
    if (journey === null || studio === null) {
      return;
    }

    const piano = studio.getPiano();

    const unsubscribe = piano.on((event) => {
      const now = performance.now();

      if (event.type === 'press') {
        if (event.origin !== 'player') {
          return;
        }

        journey.recordPianoPress(event.note.toMidi(), now, event.velocity);

        return;
      }

      if (event.type === 'release' && event.origin === 'player') {
        journey.recordPianoRelease(event.note.toMidi(), now);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [journey, studio]);
}
